/**
 * Eredin — „The Witcher: Realms Fall".
 *
 * KING OF THE WILD HUNT: kým sú porazení všetci jeho Red Riders, Eredin je
 * ENRAGED — +1 k hodnote jeho útočných aj obranných kariet a pohyb 3.
 * Veľa kariet má pri ENRAGED silnejší efekt. (Realms Fall, „Eredin")
 *
 * Pohyb 3 je `maneuverBonus` +1: keď je Eredin ENRAGED, iné figúrky už nemá,
 * takže bonus platí presne pre neho. Red Rider vrátený do hry (Unyielding
 * Hordes) ENRAGED hneď ruší — nič sa nemusí prepisovať.
 */

import type { Ctx } from '../ops.js';
import {
  askChoice,
  dealDamage,
  discardFromHand,
  drawCard,
  emit,
  gainAction,
  getMap,
  getPlayer,
  putOnDeck,
  returnFromDiscard,
} from '../ops.js';
import { neighbors } from '../map.js';
import { areEnemies } from '../players.js';
import { nextInt } from '../rng.js';
import type { FighterId, PlayerIndex } from '../types.js';
import { skirmish } from './common.js';
import { askBoost, askMove, fighterOrNull } from './helpers.js';
import type { CardEffect, EffectCtx, EffectRegistry, HeroSpecial } from './types.js';

const EREDIN = 'eredin';
const RED_RIDER = 'red-riders';

function riders(ctx: Ctx, player: PlayerIndex) {
  return ctx.state.fighters.filter((f) => f.owner === player && f.defId === RED_RIDER);
}

/** Eredin je ENRAGED, keď sú porazení všetci jeho Red Riders. */
export function isEnraged(ctx: Ctx, player: PlayerIndex): boolean {
  if (ctx.state.players[player]?.heroId !== EREDIN) return false;
  return riders(ctx, player).length === 0;
}

/** Hodnota súperovej karty sa ignoruje — bez ohľadu na to, kto koho napadol. */
function ignoreOpponentValue(ctx: Ctx, player: PlayerIndex): void {
  const combat = ctx.state.combat;
  if (!combat) return;
  (combat.flags ??= []).push(combat.attackerPlayer === player ? 'zeroDefense' : 'zeroAttack');
}

/**
 * „You may defeat a Red Rider…" — keď ich je viac, hráč vyberie, ktorého.
 * Red Riders majú 1 zdravie; poškodenie o celé zdravie je istota aj keby nie.
 */
function defeatRider(ctx: Ctx, player: PlayerIndex, source: string, label: string): void {
  const alive = riders(ctx, player);
  if (alive.length === 1) {
    dealDamage(ctx, alive[0]!.id, alive[0]!.hp);
    return;
  }
  askChoice(ctx, {
    kind: 'damage',
    player,
    source,
    label,
    optional: false,
    amount: Math.max(...alive.map((f) => f.hp)),
    targets: alive.map((f) => f.id),
  });
}

/** „This card's effects cannot be canceled. If your opponent's card has an IMMEDIATELY effect, ignore their card's value." */
const brutalStrike: CardEffect = {
  uncancelable: true,
  duringChoice: (e) => {
    const combat = e.ctx.state.combat;
    const theirs = e.role === 'attacker' ? combat?.defenseCard : combat?.attackCard;
    const def = theirs ? e.ctx.content.cards[e.ctx.state.cards[theirs]!.defId] : undefined;
    if (def?.effects?.immediate) ignoreOpponentValue(e.ctx, e.player);
  },
};

/** „Your opponent puts 1 random card from their hand on top of their deck. If Eredin is ENRAGED, they discard it instead." */
const backhand: CardEffect = {
  after: (e) => {
    const opponent = e.opponentPlayer;
    if (opponent === null) return;
    const hand = getPlayer(e.ctx.state, opponent).hand;
    if (hand.length === 0) return;
    const cardId = hand[nextInt(e.ctx.state, hand.length)]!;
    if (isEnraged(e.ctx, e.player)) discardFromHand(e.ctx, opponent, cardId, 'effect', true);
    else putOnDeck(e.ctx, opponent, cardId);
  },
};

/**
 * „If Eredin is ENRAGED, you may play this card face-up to target a fighter
 * in any space. AFTER COMBAT: You may defeat a Red Rider to return this card
 * to your hand." Návrat ide cez potvrdenie — to príde až po upratovaní súboja,
 * keď karta leží v odhode.
 */
const foulPurpose: CardEffect = {
  attackAnywhere: (ctx, player) => isEnraged(ctx, player),
  after: (e) => {
    if (riders(e.ctx, e.player).length === 0) return;
    askChoice(e.ctx, {
      kind: 'confirm',
      player: e.player,
      source: e.cardId,
      label: 'Foul Purpose: porazíš Red Ridera a vezmeš si túto kartu späť do ruky?',
      optional: true,
      effect: 'eredin.foul-purpose',
      yes: 'Poraziť Red Ridera',
      no: 'Nie',
    });
  },
  onConfirm: (ctx, choice) => {
    if (!choice.source || riders(ctx, choice.player).length === 0) return;
    const cardId = choice.source;
    defeatRider(ctx, choice.player, cardId, 'Foul Purpose: ktorého Red Ridera porazíš?');
    // Karta sa vráti, až keď je rider naozaj porazený (voľba vyššie).
    askChoice(ctx, {
      kind: 'auto',
      player: choice.player,
      source: cardId,
      label: 'Foul Purpose: karta späť do ruky',
      optional: false,
      follow: { kind: 'effect', effect: 'eredin.foul-purpose', data: cardId },
    });
  },
  onFollow: (ctx, choice, cardId) => {
    returnFromDiscard(ctx, choice.player, [cardId]);
  },
};

/**
 * „IMMEDIATELY: Return a defeated Red Rider to any space. DURING COMBAT: Add
 * +1 to this card's value for each other friendly fighter adjacent to the
 * opposing fighter."
 */
const unyieldingHordes: CardEffect = {
  immediate: (e) => {
    const fallen = (e.ctx.state.defeated ?? []).find((f) => f.owner === e.player && f.defId === RED_RIDER);
    if (!fallen) return;
    askChoice(e.ctx, {
      kind: 'place',
      player: e.player,
      source: e.cardId,
      label: 'Unyielding Hordes: vráť porazeného Red Ridera na ľubovoľný priestor',
      optional: false,
      fighterId: fallen.id,
      spaces: getMap(e.ctx).spaces.map((s) => s.id),
    });
  },
  during: (e, value) => {
    const target = fighterOrNull(e.ctx, e.opponentFighterId);
    if (!target) return value;
    const around = new Set(neighbors(getMap(e.ctx), target.spaceId));
    const helpers = e.ctx.state.fighters.filter(
      (f) => f.owner === e.player && f.id !== e.fighterId && around.has(f.spaceId),
    ).length;
    return value + helpers;
  },
};

/**
 * „Deal 1 damage to each opposing fighter that is adjacent to at least one
 * of your fighters. If Eredin is ENRAGED, deal 2 damage instead."
 */
const wildHunt: CardEffect = {
  after: (e) => {
    const map = getMap(e.ctx);
    const amount = isEnraged(e.ctx, e.player) ? 2 : 1;
    const mine = e.ctx.state.fighters.filter((f) => f.owner === e.player).map((f) => f.spaceId);
    const near = new Set(mine.flatMap((spaceId) => neighbors(map, spaceId)));
    const targets = e.ctx.state.fighters
      .filter((f) => areEnemies(e.ctx.state, f.owner, e.player) && near.has(f.spaceId))
      .map((f) => f.id);
    for (const id of targets) dealDamage(e.ctx, id, amount);
  },
};

/**
 * „IMMEDIATELY: If Eredin is ENRAGED, cancel all effects on your opponent's
 * card. DURING COMBAT: You may defeat a Red Rider to ignore the value of your
 * opponent's card."
 */
const icyGuile: CardEffect = {
  cancelsOpponentIf: (e) => isEnraged(e.ctx, e.player),
  duringChoice: (e) => {
    if (riders(e.ctx, e.player).length === 0) return;
    askChoice(e.ctx, {
      kind: 'confirm',
      player: e.player,
      source: e.cardId,
      label: 'Icy Guile: porazíš Red Ridera, aby sa hodnota súperovej karty ignorovala?',
      optional: true,
      effect: 'eredin.icy-guile',
      yes: 'Poraziť Red Ridera',
      no: 'Nie',
    });
  },
  onConfirm: (ctx, choice) => {
    if (riders(ctx, choice.player).length === 0) return;
    ignoreOpponentValue(ctx, choice.player);
    defeatRider(ctx, choice.player, choice.source ?? 'eredin.icy-guile', 'Icy Guile: ktorého Red Ridera porazíš?');
  },
};

/**
 * „IMMEDIATELY: Eredin swaps spaces with an adjacent friendly fighter. That
 * fighter is now the defender. AFTER COMBAT: If Eredin is ENRAGED, draw 2 cards."
 */
const portalDefense: CardEffect = {
  immediate: (e) => {
    const eredin = fighterOrNull(e.ctx, e.fighterId);
    if (eredin?.defId !== EREDIN) return;
    const around = new Set(neighbors(getMap(e.ctx), eredin.spaceId));
    const friends = e.ctx.state.fighters.filter((f) => f.owner === e.player && around.has(f.spaceId));
    askChoice(e.ctx, {
      kind: 'swap',
      player: e.player,
      source: e.cardId,
      label: 'Portal Defense: Eredin si vymení miesto so susednou vlastnou figúrkou — bráni sa ona',
      optional: false,
      fighterId: eredin.id,
      candidates: friends.map((f) => f.id),
    });
  },
  after: (e) => {
    if (!isEnraged(e.ctx, e.player)) return;
    drawCard(e.ctx, e.player);
    drawCard(e.ctx, e.player);
  },
};

/** „If Eredin is ENRAGED, you may play this card as an attack. DURING COMBAT: You may BOOST this card." */
const implacable: CardEffect = {
  playableAsAttack: (ctx, player, fighter) => fighter.defId === EREDIN && isEnraged(ctx, player),
  duringChoice: (e) => askBoost(e, 'Implacable: smieš boostovať túto kartu'),
};

/**
 * „Draw 1 card. Gain 1 action. ONGOING: Eredin does not take damage except
 * combat damage or from exhaustion. Discard this card at the end of your turn
 * if you didn't attack."
 */
const mightOfTheAenElle: CardEffect = {
  scheme: (e) => {
    drawCard(e.ctx, e.player);
    gainAction(e.ctx, e.player);
  },
  ongoing: {
    discardAtEnd: (ctx) => !ctx.state.turn.actionsUsed.includes('attack'),
    preventsDamage: (_ctx, _player, fighter, source) => fighter.defId === EREDIN && source === 'effect',
  },
};

/** „Draw 2 cards. Move each of your fighters up to 3 spaces each. They may move through opposing fighters." */
const closeForTheKill: CardEffect = {
  scheme: (e: EffectCtx) => {
    drawCard(e.ctx, e.player);
    drawCard(e.ctx, e.player);
    const ids: FighterId[] = e.ctx.state.fighters.filter((f) => f.owner === e.player).map((f) => f.id);
    for (const id of ids) {
      askMove(e, {
        fighters: [id],
        max: 3,
        through: true,
        label: `Close for the Kill: presuň ${fighterOrNull(e.ctx, id)?.name ?? 'figúrku'} až o 3 (smie prejsť cez súperov)`,
      });
    }
  },
};

export const eredinCards: EffectRegistry = {
  'eredin.brutal-strike': brutalStrike,
  'eredin.backhand': backhand,
  'eredin.foul-purpose': foulPurpose,
  'eredin.unyielding-hordes': unyieldingHordes,
  'eredin.wild-hunt': wildHunt,
  'eredin.skirmish': skirmish(2),
  'eredin.icy-guile': icyGuile,
  'eredin.portal-defense': portalDefense,
  'eredin.implacable': implacable,
  'eredin.might-of-the-aen-elle': mightOfTheAenElle,
  'eredin.close-for-the-kill': closeForTheKill,
};

/** KING OF THE WILD HUNT — ENRAGED: +1 k útoku aj obrane Eredina, pohyb 3. */
export const eredinSpecial: HeroSpecial = {
  name: 'King of the Wild Hunt',
  attackModifier: (ctx, player, fighterId) =>
    isEnraged(ctx, player) && fighterOrNull(ctx, fighterId)?.defId === EREDIN ? 1 : 0,
  defenseModifier: (ctx, player, fighterId) =>
    isEnraged(ctx, player) && fighterOrNull(ctx, fighterId)?.defId === EREDIN ? 1 : 0,
  maneuverBonus: (ctx, player) => (isEnraged(ctx, player) ? 1 : 0),
  onDefeated: (ctx, player, fighter) => {
    if (fighter.defId === RED_RIDER && isEnraged(ctx, player)) {
      emit(ctx, { type: 'specialUsed', player, heroId: EREDIN, name: 'ENRAGED — padli všetci Red Riders' });
    }
  },
};
